import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { CustomSuccessToast } from '@shared/components/customSuccessToast';
import { useErrorHandler } from '@shared/hooks/useErrorHandler';
import type { PermissionGroupDto, Privilege } from '@shared/services/apiService';

import { PRIVILEGE_SECTIONS, canGrant, isBuiltIn, privilegeI18nKey } from '../model/privileges';
import type { AccessControlApi } from '../model/useAccessControlApi';
import { useStyles } from './styles';

export type GroupDialogMode = 'create' | 'edit' | 'duplicate' | 'view';

interface GroupDialogProps {
  open: boolean;
  mode: GroupDialogMode;
  group: PermissionGroupDto | null;
  api: AccessControlApi;
  onClose: () => void;
}

export const GroupDialog: React.FC<GroupDialogProps> = ({ open, mode, group, api, onClose }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { showError } = useErrorHandler();

  const [name, setName] = useState('');
  const [privileges, setPrivileges] = useState<Privilege[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const readOnly = mode === 'view';
  const initialPrivileges = useMemo(
    () => (mode === 'create' ? [] : (group?.privileges ?? [])),
    [mode, group],
  );

  useEffect(() => {
    if (!open) return;
    setPrivileges(initialPrivileges);
    setName(
      mode === 'duplicate'
        ? t('accessControl.groups.dialog.copyName', { name: group?.name ?? '' })
        : mode === 'create'
          ? ''
          : (group?.name ?? ''),
    );
  }, [open, mode, group, initialPrivileges, t]);

  // A privilege the caller doesn't hold can't be switched either way — the API refuses both.
  const isLocked = (privilege: Privilege) => readOnly || !canGrant(api.actorPrivileges, privilege);
  const hasLocked = !readOnly && PRIVILEGE_SECTIONS.some((s) => s.privileges.some(isLocked));

  const toggle = (privilege: Privilege) =>
    setPrivileges((current) =>
      current.includes(privilege)
        ? current.filter((p) => p !== privilege)
        : [...current, privilege],
    );

  const trimmedName = name.trim();

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      if (mode === 'edit' && group) {
        await api.updateGroup(group.id, { name: trimmedName, privileges });
      } else {
        await api.createGroup({ name: trimmedName, privileges });
      }
      toast.success(CustomSuccessToast, {
        closeButton: false,
        autoClose: 4000,
        icon: false,
        data: {
          title: t('accessControl.groups.toast.savedTitle'),
          description: t('accessControl.groups.toast.saved', { name: trimmedName }),
        },
      });
      onClose();
    } catch (error) {
      showError({ title: t('accessControl.groups.errors.saveTitle'), error });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{ className: classes.modalPaper }}>
      <DialogTitle>{t(`accessControl.groups.dialog.title.${mode}`)}</DialogTitle>
      <DialogContent dividers className={classes.modalContent}>
        {group && isBuiltIn(group) && (
          <Alert severity="info">{t('accessControl.groups.dialog.builtInNotice')}</Alert>
        )}
        {group && group.directoryGroups.length > 0 && (
          <Alert severity="info">
            {t('accessControl.groups.dialog.directoryNotice', {
              groups: group.directoryGroups.join(', '),
            })}
          </Alert>
        )}
        <TextField
          label={t('accessControl.groups.dialog.name')}
          value={name}
          onChange={(event) => setName(event.target.value)}
          disabled={readOnly}
          required
          fullWidth
          inputProps={{ 'data-testid': 'permission-group-name' }}
        />
        {hasLocked && (
          <Typography variant="body2" color="text.secondary">
            {t('accessControl.groups.dialog.lockedHint')}
          </Typography>
        )}
        {PRIVILEGE_SECTIONS.map((section) => (
          <Box key={section.key} className={classes.section}>
            <Typography variant="subtitle2" className={classes.sectionTitle}>
              {t(`accessControl.privilegeSections.${section.key}`)}
            </Typography>
            {section.privileges.map((privilege) => {
              const locked = isLocked(privilege);
              const control = (
                <FormControlLabel
                  className={classes.option}
                  disabled={locked}
                  control={
                    <Checkbox
                      checked={privileges.includes(privilege)}
                      onChange={() => toggle(privilege)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">
                        {t(`${privilegeI18nKey(privilege)}.label`)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t(`${privilegeI18nKey(privilege)}.description`)}
                      </Typography>
                    </Box>
                  }
                />
              );

              return locked && !readOnly ? (
                <Tooltip
                  key={privilege}
                  title={t('accessControl.groups.dialog.lockedTooltip')}
                  placement="top-start">
                  <Box>{control}</Box>
                </Tooltip>
              ) : (
                <Box key={privilege}>{control}</Box>
              );
            })}
          </Box>
        ))}
      </DialogContent>
      <DialogActions className={classes.modalActions}>
        <Button onClick={onClose} disabled={isSubmitting}>
          {readOnly ? t('accessControl.common.close') : t('accessControl.common.cancel')}
        </Button>
        {!readOnly && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting || !trimmedName}
            data-testid="permission-group-save">
            {isSubmitting ? t('accessControl.common.saving') : t('accessControl.common.save')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
